/**
 * Extract fillable field ids and signature block ids from editorJson (ProseMirror/TipTap).
 * Used by Fill view and PDF export to know which fields/signatures exist.
 */

/**
 * Recursively walk node content and collect fillable field refs and signature block refs.
 * @param {Object} node - ProseMirror node (has type, attrs, content, marks)
 * @returns {{ fields: Array<{ id: string, type: string }>, signatureBlocks: Array<{ id: string, label: string }> }}
 */
function collectFromNode(node) {
  const fields = [];
  const signatureBlocks = [];

  if (!node) return { fields, signatureBlocks };

  // Marks on this node (e.g. fillableField on text)
  if (Array.isArray(node.marks)) {
    for (const mark of node.marks) {
      if (mark.type === 'fillableField' && mark.attrs?.fieldId) {
        fields.push({
          id: mark.attrs.fieldId,
          type: mark.attrs.fieldType || 'text',
          label: mark.attrs.label || '',
        });
      }
    }
  }

  // Node itself (placeholder has no "previous text" label)
  if (node.type === 'fillableFieldPlaceholder' && node.attrs?.fieldId) {
    fields.push({
      id: node.attrs.fieldId,
      type: node.attrs.fieldType || 'text',
      label: node.attrs.label || '',
    });
  }
  if (node.type === 'signatureBlock' && node.attrs?.blockId) {
    const at = node.attrs.assigneeType;
    let assigneeType = 'me';
    if (at === 'someone_else' || at === 'me') {
      assigneeType = at;
    } else if (node.attrs.assigneeEmail && String(node.attrs.assigneeEmail).trim()) {
      // Legacy blocks without assigneeType: a saved email implies an external signer.
      assigneeType = 'someone_else';
    } else {
      assigneeType = 'me';
    }
    signatureBlocks.push({
      id: node.attrs.blockId,
      label: node.attrs.label || 'Sign here',
      assigneeEmail: node.attrs.assigneeEmail || '',
      assigneeType,
      signingOrderIndex: node.attrs.signingOrderIndex ?? null,
      signatureAlign:
        node.attrs.signatureAlign === 'left' || node.attrs.signatureAlign === 'right'
          ? node.attrs.signatureAlign
          : 'full',
      defaultSignature: node.attrs.defaultSignature || '',
      defaultSignerName: node.attrs.defaultSignerName || '',
      defaultSignerEmail: node.attrs.defaultSignerEmail || '',
      defaultSignedAt: node.attrs.defaultSignedAt || '',
    });
  }

  // Recurse into content
  if (Array.isArray(node.content)) {
    for (const child of node.content) {
      const childResult = collectFromNode(child);
      fields.push(...childResult.fields);
      signatureBlocks.push(...childResult.signatureBlocks);
    }
  }

  return { fields, signatureBlocks };
}

/**
 * Extract all fillable field ids and signature block ids from editorJson.
 * Deduplicates by id (same field id can appear once).
 * @param {Object} editorJson - Full doc JSON (root has type 'doc' and content array)
 * @returns {{ fields: Array<{ id: string, type: string }>, signatureBlocks: Array<{ id: string, label: string }> }}
 */
export function extractFieldsAndSignatures(editorJson) {
  const fieldsMap = new Map();
  const signatureMap = new Map();

  if (!editorJson || !editorJson.content) {
    return { fields: [], signatureBlocks: [] };
  }

  for (const node of editorJson.content) {
    const { fields, signatureBlocks } = collectFromNode(node);
    for (const f of fields) {
      if (!fieldsMap.has(f.id)) fieldsMap.set(f.id, f);
    }
    for (const s of signatureBlocks) {
      if (!signatureMap.has(s.id)) signatureMap.set(s.id, s);
    }
  }

  return {
    fields: Array.from(fieldsMap.values()),
    signatureBlocks: Array.from(signatureMap.values()),
  };
}

/**
 * Recursively remove signature blocks and fillable fields from editor JSON.
 * - Removes nodes with type 'signatureBlock' or 'fillableFieldPlaceholder'
 * - Strips the 'fillableField' mark from any node
 * Use when "Discard plates & fields" is chosen (document must not be signed yet).
 * @param {Object} editorJson - Full doc JSON (type 'doc', content array)
 * @returns {Object} New editor JSON with signature plates and fields removed (shallow copy where unchanged)
 */
export function stripSignatureBlocksAndFields(editorJson) {
  if (!editorJson || typeof editorJson !== 'object') return editorJson;

  function processNode(node) {
    if (!node) return null;
    if (node.type === 'signatureBlock') return null;
    if (node.type === 'fillableFieldPlaceholder') return null;

    const out = { ...node };
    if (Array.isArray(out.marks)) {
      out.marks = out.marks.filter((m) => m && m.type !== 'fillableField');
      if (out.marks.length === 0) delete out.marks;
    }
    if (Array.isArray(out.content)) {
      out.content = out.content.map(processNode).filter(Boolean);
    }
    return out;
  }

  const doc = { ...editorJson };
  if (Array.isArray(doc.content)) {
    doc.content = doc.content.map(processNode).filter(Boolean);
    if (doc.content.length === 0) {
      doc.content = [{ type: 'paragraph', content: [] }];
    }
  }
  return doc;
}

export default extractFieldsAndSignatures;
